import {FormEvent, useCallback, useState} from 'react';
import {useHistory} from 'react-router-dom';
import QRCode from 'qrcode.react';
import {gql, useMutation} from '@apollo/client';
import styles from './verify.module.scss';
import {TextInput} from '../../components/text-input/TextInput';
import AuthPageLayout from '../../components/auth-page-layout/AuthPageLayout';
import {useAuthToken} from '../../hooks/useAuthToken';
import Checkbox from '../../components/checkbox/Checkbox';
import { useAnalytics, Events } from '@hooks';
import { getOrCreateSessionId, setSessionUserId } from '../../utils/sentryTrackingConfig';
import { resetLogoutTracking } from '../../utils/logoutTrackingGuard';
import { fetchMeId } from '../../utils/fetchMeWithToken';
import { apiUrl } from '@constants';
import { logger } from '../../utils/logger';

type Props = {};

const VerifyMutation = gql`
  mutation verifyCode($input: VerifyCodeInput!) {
    verifyCode(input: $input)
  }
`

function Verify(props: Props) {
  const [mfa_code, setMFACode] = useState('');
  const { setAuthToken, removeAuthToken } = useAuthToken();
  const { push, location } = useHistory();
  const { trackEvent, segment } = useAnalytics();
  const [showKey, setShowKey] = useState(false);
  const [disableMFA, setDisableMFA] = useState(false);

  const [verify, {loading, error}] = useMutation<{ verifyCode: string }>(VerifyMutation, {
    onCompleted: async (data) => {
      const token = data.verifyCode;
      const userId = apiUrl ? await fetchMeId(apiUrl, token) : undefined;
      const state = location.state as { email?: string } | undefined;
      const email = state?.email;
      if (email) {
        resetLogoutTracking();
        const sessionId = getOrCreateSessionId();
        const timestamp = new Date().toISOString();
        if (userId) setSessionUserId(userId);
        const d = { email, loginType: '2FA' as const, sessionId, timestamp, userId };
        if (userId) {
          await segment?.identify(userId, { email });
        }
        trackEvent(Events.actions.logIn, d);
        trackEvent(Events.actions.sessionStarted, d);
      }
      setAuthToken(token);
      push('/');
    },
    onError: (error) => {
      logger.debug(`[Error]`, error);
    },
  });

  const handleChange = useCallback((value) => {
    setMFACode(value);
  }, [])

  const state: any = location.state;

  if (!state) {
    push('/');
    return null;
  } else {
    const totpUri = state.totpUri || '';
    const totpSecret = totpUri.substring(totpUri.indexOf('?secret=') + 8, totpUri.indexOf('&issuer'));

    const handleSubmit = async (e: FormEvent) => {
      e.preventDefault();
      removeAuthToken();
      try {
        await verify({
          variables: {
            input: {
              code: mfa_code,
              disable: disableMFA,
              email: state.email,
              clientId: localStorage.getItem('clientId')
            }
          }
        });
      } catch (e) {
        logger.info(e)
      }
    }

    const toggleKey = (e: FormEvent) => {
      e.preventDefault();
      setShowKey(!showKey);
    }

    const handleDisableMFA = () => {
      setDisableMFA(!disableMFA);
    }

    return (
      <AuthPageLayout onSubmit={handleSubmit} loading={loading} title={'Multi-factor Authentication'}
                      buttonTitle={'Verify'}>
        {totpUri === '' ? '' : (
          <div>
            <div className={styles.description}>
              Please install a compatible authenticator app on your mobile device and scan QR code.
            </div>
            <div>
              <QRCode
                value={totpUri}
                size={300}
                level={"H"}
                includeMargin={true}
                className={styles.qrcode}
              />
            </div>
            <div className={styles.description}>
              Alternatively, you can type the secret key. <span className={styles.showKeyLink} onClick={toggleKey}>{showKey ? 'Hide' : 'Show'} key</span>
            </div>
            <pre>{showKey ? totpSecret : ' '}</pre>
          </div>
        )}
        <div className={`${styles.description} ${styles.secretkey}`}>
          Enter a MFA code to complete sign-in.
        </div>
        <TextInput allowSpecialCharacters placeholder={'Code'} label={''} id={'mfa_code'} value={mfa_code} onChange={handleChange} autoFocus={true}/>
        <div className={styles.checkboxWrapper}>
          <Checkbox id={'disableMFA'} label={'Disable Two-Factor-Authentication (2FA) on this computer for 30 days.'} checked={disableMFA} onChange={handleDisableMFA}/>
        </div>
        <div className={styles.actionsWrapper}>
          <div className={styles.error}>
            {error?.message}
          </div>
        </div>
      </AuthPageLayout>
    );
  }
}

export default Verify;
